import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Folder, 
  StickyNote as StickyIcon, 
  FileText, 
  Image as ImageIcon, 
  Link as LinkIcon,
  Loader2,
  File,
  ExternalLink,
  Play,
  Film,
  Plus,
  Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MindfullLogo from '@/components/MindfullLogo';
import { toast } from 'sonner';

interface ShareData {
  id: string;
  share_type: 'item' | 'board';
  item_id: string | null;
  board_id: string | null;
  shared_by_name: string;
  view_count: number;
}

interface SharePageProps {
  shareType?: 'board';
}

export default function SharePage({ shareType }: SharePageProps) {
  const { token } = useParams<{ token: string }>();
  const [share, setShare] = useState<ShareData | null>(null);
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchShare = async () => {
      try {
        setLoading(true);

        if (shareType === 'board') {
          // Board share — query board_shares table
          const { data: boardShareData, error: shareError } = await supabase
            .from('board_shares')
            .select('*')
            .eq('share_token', token)
            .eq('is_active', true)
            .maybeSingle();

          if (shareError || !boardShareData) {
            setError("This share link doesn't exist or has expired.");
            setLoading(false);
            return;
          }

          // Check expiration
          if (boardShareData.expires_at && new Date(boardShareData.expires_at) < new Date()) {
            setError("This share link has expired.");
            setLoading(false);
            return;
          }

          // Set share data in the format the component expects
          setShare({
            id: boardShareData.id,
            share_type: 'board',
            item_id: null,
            board_id: boardShareData.board_id,
            shared_by_name: 'A Mindfull user',
            view_count: 0,
          });

          // Fetch the board and its items
          const { data: board, error: boardError } = await supabase
            .from('boards')
            .select('*')
            .eq('id', boardShareData.board_id)
            .maybeSingle();

          if (boardError || !board) {
            setError("The shared board could not be found.");
          } else {
            const { data: items } = await supabase
              .from('items')
              .select('*')
              .eq('board_id', boardShareData.board_id)
              .order('z_index', { ascending: true });

            setContent({ board, items: items || [] });
          }
        } else {
          // Item share — query shares table (existing logic)
          const { data: shareData, error: shareError } = await supabase
            .from('shares')
            .select('*')
            .eq('share_token', token)
            .maybeSingle();
          
          if (shareError || !shareData) {
            setError("This share link doesn't exist or has expired.");
            setLoading(false);
            return;
          }
          
          setShare(shareData as unknown as ShareData);
          
          // Increment view count
          await supabase
            .from('shares')
            .update({ view_count: shareData.view_count + 1 })
            .eq('id', shareData.id);
          
          // Fetch the actual content
          if (shareData.share_type === 'item') {
            const { data: item, error: itemError } = await supabase
              .from('items')
              .select('*')
              .eq('id', shareData.item_id)
              .maybeSingle();
            
            if (itemError || !item) {
              setError("The shared item could not be found.");
            } else {
              setContent(item);
            }
          } else if (shareData.share_type === 'board') {
            const { data: board, error: boardError } = await supabase
              .from('boards')
              .select('*')
              .eq('id', shareData.board_id)
              .maybeSingle();
            
            if (boardError || !board) {
              setError("The shared board could not be found.");
            } else {
              const { data: items } = await supabase
                .from('items')
                .select('*')
                .eq('board_id', shareData.board_id)
                .order('z_index', { ascending: true });
              
              setContent({ board, items: items || [] });
            }
          }
        }
      } catch (err) {
        console.error('Share fetch error:', err);
        setError("An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    };
    
    if (token) fetchShare();
  }, [token, shareType]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-200 dark:bg-stone-900">
        <Loader2 className="w-8 h-8 animate-spin text-partnr-plain" />
      </div>
    );
  }

  if (error || !share) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-200 dark:bg-stone-900 p-6 text-center">
        <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl p-12 max-w-md">
          <h1 className="text-2xl font-bold mb-4 text-stone-900 dark:text-white">Not Found</h1>
          <p className="text-stone-500 dark:text-stone-400 mb-8">{error || "This share link doesn't exist."}</p>
          <Button asChild className="bg-partnr-static text-partnr-foreground hover:opacity-90">
            <Link to="/">Go to Mindfull</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-200 dark:bg-stone-900">
      {share.share_type === 'item' ? (
        <ItemShareLayout share={share} item={content} />
      ) : (
        <BoardShareLayout share={share} board={content.board} items={content.items} />
      )}
    </div>
  );
}

// MyMind-style item share layout - split screen
const ItemShareLayout = ({ share, item }: { share: ShareData; item: any }) => {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left: Large Content Preview */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <ContentPreview item={item} />
      </div>
      
      {/* Right: Info Sidebar */}
      <div className="w-full lg:w-80 bg-white dark:bg-stone-800 shadow-2xl flex flex-col lg:min-h-screen">
        <InfoSidebar share={share} item={item} />
      </div>
    </div>
  );
};

// Board share layout - full width with grid
const BoardShareLayout = ({ share, board, items }: { share: ShareData; board: any; items: any[] }) => {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-stone-800 border-b border-stone-200 dark:border-stone-700 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <MindfullLogo size="sm" />
            </Link>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild className="hidden sm:flex">
              <Link to="/login">Sign in</Link>
            </Button>
            <Button size="sm" asChild className="bg-partnr-static text-partnr-foreground hover:opacity-90">
              <Link to="/signup">Create Your Own</Link>
            </Button>
          </div>
        </div>
      </header>
      
      {/* Board Content */}
      <main className="flex-1 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Board header */}
          <div className="flex items-center gap-4 mb-12">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ backgroundColor: board.icon_color || '#6366F1' }}
            >
              <Folder className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-stone-900 dark:text-white">{board.name}</h1>
              <p className="text-stone-500 dark:text-stone-400">
                Shared by {share.shared_by_name} · {items.length} items
              </p>
            </div>
          </div>
          
          {/* Items grid */}
          {items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map(item => (
                <BoardItemCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="text-center py-24 bg-white dark:bg-stone-800 rounded-3xl">
              <Folder className="w-12 h-12 text-stone-300 dark:text-stone-600 mx-auto mb-4" />
              <p className="text-stone-500 dark:text-stone-400 text-lg">This board is empty</p>
            </div>
          )}
        </div>
      </main>
      
      {/* CTA Banner */}
      <div className="bg-white dark:bg-stone-800 border-t border-stone-200 dark:border-stone-700 py-8 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-stone-600 dark:text-stone-400 mb-4">
            Create your own infinite canvas to organize your thoughts
          </p>
          <Button asChild className="bg-partnr-static text-partnr-foreground hover:opacity-90">
            <Link to="/signup">Get Started Free</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

// Large content preview component (MyMind-style)
const ContentPreview = ({ item }: { item: any }) => {
  if (!item) return null;
  
  switch (item.item_type) {
    case 'image':
      return (
        <div className="max-w-4xl w-full">
          <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl overflow-hidden">
            <img 
              src={item.content.url} 
              alt={item.content.filename}
              className="w-full h-auto max-h-[70vh] object-contain"
            />
          </div>
        </div>
      );
    
    case 'sticky':
      const stickyBgColor = getStickyColor(item.content.color, item.content.customColor);
      const stickyTextColor = item.content.customColor ? getContrastColorForShare(item.content.customColor) : '#1f2937';
      return (
        <div 
          className="p-12 rounded-2xl shadow-2xl max-w-lg w-full aspect-square flex flex-col items-center justify-center relative"
          style={{ backgroundColor: stickyBgColor }}
        >
          <p className="text-2xl leading-relaxed text-center whitespace-pre-wrap" style={{ color: stickyTextColor }}>
            {item.content.text}
          </p>
          {item.content.colorCode && (
            <div 
              className="absolute bottom-4 right-4 text-xs font-mono"
              style={{ opacity: 0.35, color: stickyTextColor }}
            >
              {item.content.colorCode}
            </div>
          )}
        </div>
      );
    
    case 'text_card':
      return (
        <div className="bg-white dark:bg-stone-800 p-12 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          <div className="prose prose-lg dark:prose-invert max-w-none prose-stone">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {item.content.body}
            </ReactMarkdown>
          </div>
        </div>
      );
    
    case 'document':
      return (
        <div className="bg-white dark:bg-stone-800 p-12 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
          <h1 className="text-3xl font-bold mb-8 text-stone-900 dark:text-white">{item.content.title}</h1>
          <div 
            className="prose prose-lg dark:prose-invert max-w-none prose-stone"
            dangerouslySetInnerHTML={{ __html: item.content.body }}
          />
        </div>
      );
    
    case 'link':
      return (
        <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
          {item.content.image && (
            <img 
              src={item.content.image} 
              alt="" 
              className="w-full h-64 object-cover"
            />
          )}
          <div className="p-8">
            <div className="text-sm text-partnr-plain font-medium mb-2">
              {item.content.siteName || new URL(item.content.url).hostname}
            </div>
            <h2 className="text-2xl font-bold mb-3 text-stone-900 dark:text-white leading-tight">
              {item.content.title || 'Untitled Link'}
            </h2>
            {item.content.description && (
              <p className="text-stone-600 dark:text-stone-400 mb-4 leading-relaxed">
                {item.content.description}
              </p>
            )}
            <a 
              href={item.content.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-partnr-plain hover:underline"
            >
              Visit link <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      );
    
    case 'file':
      return (
        <div className="bg-white dark:bg-stone-800 p-12 rounded-2xl shadow-2xl max-w-md w-full text-center">
          <div className="w-20 h-20 bg-stone-100 dark:bg-stone-700 rounded-2xl mx-auto mb-6 flex items-center justify-center">
            <File className="w-10 h-10 text-stone-400" />
          </div>
          <h2 className="text-xl font-semibold text-stone-900 dark:text-white mb-2">
            {item.content.filename || 'File'}
          </h2>
          <p className="text-stone-500 dark:text-stone-400 text-sm mb-6">
            {formatFileSize(item.content.size)}
          </p>
          {item.content.url && (
            <Button asChild className="bg-partnr-static text-partnr-foreground hover:opacity-90">
              <a href={item.content.url} target="_blank" rel="noopener noreferrer">
                Download File
              </a>
            </Button>
          )}
        </div>
      );
    
    case 'video':
      return (
        <div className="max-w-4xl w-full">
          <div className="bg-black rounded-2xl shadow-2xl overflow-hidden">
            {item.content.videoUrl ? (
              <video
                src={item.content.videoUrl}
                controls
                className="w-full max-h-[70vh] object-contain"
                poster={item.content.thumbnailUrl}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="w-full h-64 flex items-center justify-center">
                <Film className="w-16 h-16 text-stone-400" />
              </div>
            )}
          </div>
          <p className="text-center text-stone-500 dark:text-stone-400 mt-4">
            {item.content.filename || 'Video'}
          </p>
        </div>
      );
    
    default:
      return (
        <div className="bg-white dark:bg-stone-800 p-12 rounded-2xl shadow-2xl text-center">
          <p className="text-stone-500 dark:text-stone-400">Preview not available</p>
        </div>
      );
  }
};

// Info sidebar component with "Add to Mindfull" functionality
const InfoSidebar = ({ share, item }: { share: ShareData; item: any }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useParams<{ token: string }>();
  
  useEffect(() => {
    checkAuth();
  }, []);
  
  useEffect(() => {
    // Auto-add if redirected back after login with action=add
    if (searchParams.get('action') === 'add' && user && !isAdding) {
      handleAddToMindfull();
    }
  }, [user, searchParams]);
  
  const checkAuth = async () => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    setUser(authUser);
  };
  
  const handleAddToMindfull = async () => {
    if (!user) {
      // Redirect to login with return URL - use token from URL params
      const returnUrl = `/share/${token}?action=add`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }
    
    setIsAdding(true);
    
    try {
      // Clone item to user's Wall (no board)
      const { error } = await supabase
        .from('items')
        .insert({
          user_id: user.id,
          board_id: null, // Add to Wall, not a specific board
          item_type: item.item_type,
          position_x: 0,
          position_y: 0,
          width: item.width,
          height: item.height,
          content: {
            ...item.content,
            cloned_from: item.id,
            cloned_at: new Date().toISOString()
          }
        });
      
      if (error) throw error;
      toast.success('Added to your Wall!');
      
      // Redirect to wall after a short delay
      setTimeout(() => {
        navigate('/canvas?view=wall');
      }, 1500);
      
    } catch (err) {
      console.error(err);
      toast.error('Failed to add item');
    } finally {
      setIsAdding(false);
    }
  };

  const getItemTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'image': 'Image',
      'sticky': 'Note',
      'text_card': 'Text',
      'document': 'Document',
      'link': 'Link',
      'file': 'File'
    };
    return labels[type] || 'Item';
  };
  
  const getItemTypeIcon = (type: string) => {
    switch (type) {
      case 'image': return <ImageIcon className="w-4 h-4" />;
      case 'sticky': return <StickyIcon className="w-4 h-4" />;
      case 'text_card': return <FileText className="w-4 h-4" />;
      case 'document': return <FileText className="w-4 h-4" />;
      case 'link': return <LinkIcon className="w-4 h-4" />;
      case 'file': return <File className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };
  
  const getItemTitle = (item: any) => {
    if (!item) return 'Untitled';
    
    switch (item.item_type) {
      case 'document':
        return item.content.title || 'Untitled Document';
      case 'sticky':
        return item.content.text?.slice(0, 50) || 'Note';
      case 'text_card':
        return item.content.body?.split('\n')[0]?.slice(0, 50) || 'Text';
      case 'image':
        return item.content.filename || 'Image';
      case 'link':
        return item.content.title || item.content.url;
      case 'file':
        return item.content.filename || 'File';
      default:
        return 'Untitled';
    }
  };
  
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-stone-200 dark:border-stone-700">
        <Link to="/" className="flex items-center gap-2">
          <MindfullLogo size="sm" />
        </Link>
      </div>
      
      {/* Content info */}
      <div className="flex-1 p-6 flex flex-col items-center justify-center text-center">
        {/* Type badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 dark:bg-stone-700 rounded-full text-sm text-stone-600 dark:text-stone-300 mb-6">
          {getItemTypeIcon(item?.item_type)}
          <span>{getItemTypeLabel(item?.item_type)}</span>
        </div>
        
        {/* Title */}
        <h1 className="text-xl font-bold text-stone-900 dark:text-white mb-4 leading-tight line-clamp-3">
          {getItemTitle(item)}
        </h1>
        
        {/* Shared by */}
        <p className="text-stone-500 dark:text-stone-400 mb-8">
          Shared from the mind of<br />
          <span className="font-medium text-stone-700 dark:text-stone-300">{share?.shared_by_name || 'Someone'}</span>
        </p>
        
        {/* Add to Mindfull button */}
        <Button
          onClick={handleAddToMindfull}
          disabled={isAdding}
          className="w-full py-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base rounded-xl mb-4"
        >
          {isAdding ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <Plus className="w-5 h-5 mr-2" />
              Add to Mindfull
            </>
          )}
        </Button>
        
        {/* Already have account */}
        {!user && (
          <Link
            to="/login"
            className="text-sm text-stone-500 dark:text-stone-400 hover:text-primary transition-colors"
          >
            Already have an account? Sign in
          </Link>
        )}
      </div>
      
      {/* Footer */}
      <div className="p-6 border-t border-stone-200 dark:border-stone-700 text-center">
        <div className="w-12 h-px bg-stone-300 dark:bg-stone-600 mx-auto mb-4" />
        <p className="text-xs text-stone-400 dark:text-stone-500 leading-relaxed">
          Create an account to save and<br />
          organize your own ideas.
        </p>
      </div>
    </div>
  );
};
// Board item card for grid view
const BoardItemCard = ({ item }: { item: any }) => {
  const getPreview = () => {
    switch (item.item_type) {
      case 'sticky': 
        const stickyBg = getStickyColor(item.content.color, item.content.customColor);
        const stickyText = item.content.customColor ? getContrastColorForShare(item.content.customColor) : '#1f2937';
        return (
          <div 
            className="h-40 flex items-center justify-center p-4 text-center relative"
            style={{ backgroundColor: stickyBg }}
          >
            <p className="text-sm line-clamp-4" style={{ color: stickyText }}>{item.content.text}</p>
            {item.content.colorCode && (
              <div 
                className="absolute bottom-2 right-2 text-[8px] font-mono"
                style={{ opacity: 0.3, color: stickyText }}
              >
                {item.content.colorCode}
              </div>
            )}
          </div>
        );
      case 'text_card':
        return (
          <div className="h-40 p-4 bg-white dark:bg-stone-800 text-xs text-stone-600 dark:text-stone-400 overflow-hidden">
            <div className="prose prose-xs dark:prose-invert line-clamp-6">
              <ReactMarkdown>{item.content.body}</ReactMarkdown>
            </div>
          </div>
        );
      case 'image':
        return (
          <div className="h-40 bg-stone-100 dark:bg-stone-700">
            <img src={item.content.url} alt="" className="w-full h-full object-cover" />
          </div>
        );
      case 'link':
        return (
          <div className="h-40 bg-stone-100 dark:bg-stone-700 flex flex-col">
            {item.content.image ? (
              <img src={item.content.image} alt="" className="w-full h-28 object-cover" />
            ) : (
              <div className="w-full h-28 flex items-center justify-center">
                <LinkIcon className="w-8 h-8 text-stone-300 dark:text-stone-600" />
              </div>
            )}
            <div className="p-2 text-xs font-medium text-stone-700 dark:text-stone-300 truncate">
              {item.content.title || item.content.url}
            </div>
          </div>
        );
      case 'document':
        return (
          <div className="h-40 p-4 bg-white dark:bg-stone-800">
            <h4 className="font-semibold text-stone-900 dark:text-white mb-2 line-clamp-2">
              {item.content.title}
            </h4>
            <p className="text-xs text-stone-500 dark:text-stone-400 line-clamp-3">
              {item.content.body?.replace(/<[^>]*>/g, '').slice(0, 150)}
            </p>
          </div>
        );
      case 'video':
        return (
          <div className="h-40 bg-stone-900 relative flex items-center justify-center">
            {item.content.thumbnailUrl ? (
              <img src={item.content.thumbnailUrl} alt="" className="w-full h-full object-cover opacity-80" />
            ) : (
              <Film className="w-8 h-8 text-stone-500" />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                <Play className="w-5 h-5 text-stone-800 ml-0.5" fill="currentColor" />
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="h-40 bg-stone-100 dark:bg-stone-700 flex items-center justify-center">
            <FileText className="w-8 h-8 text-stone-300 dark:text-stone-600" />
          </div>
        );
    }
  };

  const getIcon = () => {
    switch (item.item_type) {
      case 'sticky': return <StickyIcon className="w-3.5 h-3.5" />;
      case 'text_card': return <FileText className="w-3.5 h-3.5" />;
      case 'document': return <FileText className="w-3.5 h-3.5" />;
      case 'image': return <ImageIcon className="w-3.5 h-3.5" />;
      case 'link': return <LinkIcon className="w-3.5 h-3.5" />;
      case 'video': return <Film className="w-3.5 h-3.5" />;
      default: return <FileText className="w-3.5 h-3.5" />;
    }
  };

  return (
    <div className="bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 shadow-sm hover:shadow-lg transition-all overflow-hidden group">
      {getPreview()}
      <div className="p-3 border-t border-stone-100 dark:border-stone-700">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-medium text-stone-400 dark:text-stone-500">
          {getIcon()}
          <span>{item.item_type.replace('_', ' ')}</span>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getStickyColor = (color: string, customColor?: string): string => {
  if (customColor) return customColor;
  const colors: Record<string, string> = {
    yellow: '#F3E7B3',
    blue: '#D5E3F0',
    pink: '#F0D9D6',
    green: '#D9E4D2',
    purple: '#EDE9FE',
    orange: '#FFEDD5',
  };
  return colors[color] || colors.yellow;
};

const getContrastColorForShare = (bgColor: string): string => {
  // For named colors and hex, create a temporary element to resolve the color
  const el = document.createElement('div');
  el.style.color = bgColor;
  document.body.appendChild(el);
  const computed = getComputedStyle(el).color;
  document.body.removeChild(el);

  // Parse RGB from computed style
  const match = computed.match(/\d+/g);
  if (!match) return '#1a1a1a';

  const [r, g, b] = match.map(Number);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1a1a1a' : '#ffffff';
};

const formatFileSize = (bytes: number): string => {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};
